import { ref, shallowRef } from 'vue'
import type MartinBridge from '@/types/martin-bridge'

// Singleton bridge instance
let bridgeInstance: MartinBridge.MartinBridge | null = null
const isLoaded = ref(false)
const loadError = ref<string | null>(null)

/**
 * Get or create the MartinBridge instance.
 * The bridge is loaded from the global window object where Kotlin/JS exports it.
 */
export function useKotlinBridge() {
  const bridge = shallowRef<MartinBridge.MartinBridge | null>(null)

  const loadBridge = (): MartinBridge.MartinBridge | null => {
    if (bridgeInstance) {
      bridge.value = bridgeInstance
      return bridgeInstance
    }

    try {
      // Access the Kotlin/JS exported module - try multiple paths
      let kotlinModule = (window as any).tech?.mobiledeveloper?.martin?.bridge

      // If not found directly on window, try the module name path
      if (!kotlinModule) {
        const martinModule = (window as any)['Martin:web-bridge']
        kotlinModule = martinModule?.tech?.mobiledeveloper?.martin?.bridge
      }

      if (!kotlinModule) {
        console.warn('Kotlin bridge not loaded yet, trying alternative path...')
        // Try alternative path for library mode
        const martinBridge = (window as any).MartinBridge || (window as any).martinBridge

        if (martinBridge) {
          bridgeInstance = new martinBridge.MartinBridge()
          bridge.value = bridgeInstance
          isLoaded.value = true
          return bridgeInstance
        }

        loadError.value = 'Martin bridge not found. Make sure martin-bridge.js is loaded.'
        return null
      }

      bridgeInstance = new kotlinModule.MartinBridge()
      bridge.value = bridgeInstance
      isLoaded.value = true
      return bridgeInstance
    } catch (error) {
      console.error('Failed to load Kotlin bridge:', error)
      loadError.value = error instanceof Error ? error.message : 'Failed to load bridge'
      return null
    }
  }

  // Try to load immediately
  if (!bridgeInstance) {
    loadBridge()
  } else {
    bridge.value = bridgeInstance
  }

  return {
    bridge,
    isLoaded,
    loadError,
    loadBridge
  }
}

/**
 * Get the bridge instance synchronously (throws if not loaded)
 */
export function getBridge(): MartinBridge.MartinBridge {
  if (!bridgeInstance) {
    const { loadBridge } = useKotlinBridge()
    const loaded = loadBridge()
    if (!loaded) {
      throw new Error('Martin bridge not available')
    }
    return loaded
  }
  return bridgeInstance
}
